import React, { useState } from 'react';
import { Play, MessageCircle, Youtube, Users, Twitch, Euro, Gem } from 'lucide-react';
import { Link } from 'react-router-dom';
import { streamer, brand } from '../mock';
import { useTwitchChannel } from '../hooks/useTwitch';
import { useAuth } from '../context/AuthContext';
import { useBio } from '../hooks/useBio';
import useMascot from '../hooks/useMascot';
import LatestStatsGrid from './LatestStatsGrid';
import Subgoal from './Subgoal';
import HeartRateBubble from './HeartRateBubble';
import HeroSpeechBubble from './HeroSpeechBubble';
import HeroSocials from './HeroSocials';
import HomeMultiChat from './HomeMultiChat';
import { LiveStatusBadge } from './nav/NavParts';

export default function Hero() {
  const { live, stats, data, loading } = useTwitchChannel();
  const { user, login } = useAuth();
  const { bio } = useBio();
  const { url: mascotUrl } = useMascot();
  const broadcasterName = data?.broadcaster?.display_name || 'JorDievoorje';
  const broadcasterLogin = (
    data?.broadcaster?.login || 'jordievoorje'
  ).toLowerCase();
  const [playerOn, setPlayerOn] = useState(false);
  // Twitch requires every domain that embeds its player to be listed as
  // a `parent` query param. Deriving it from window.location.hostname
  // covers preview + production without a manual allow-list.
  const parentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const twitchEmbedSrc =
    `https://player.twitch.tv/?channel=${broadcasterLogin}` +
    `&parent=${parentHost}&muted=true&autoplay=true`;

  return (
    <section
      id="home"
      className="relative min-h-screen pb-16 overflow-hidden"
      style={{ paddingTop: 'calc(6rem + var(--stats-ticker-top-h, 0px) + var(--stats-ticker-gap, 0px))' }}
    >
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-40 right-0 w-[420px] h-[420px] rounded-full bg-fuchsia-500/10 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-orange-500/10 blur-[120px]" />
      </div>

      <div
        className="absolute inset-0 -z-10 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '52px 52px'
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-stretch">
        <div className="relative flex flex-col">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
            <span
              className={`w-2 h-2 rounded-full ${
                live.isLive ? 'bg-red-400 animate-pulse' : 'bg-zinc-500'
              }`}
            />
            <span className="text-xs text-zinc-300">
              {live.isLive
                ? `NU LIVE \u2022 ${live.viewers} kijkers`
                : 'Offline \u2014 check het schema voor de volgende stream'}
            </span>
          </div>

          <div className="relative inline-block w-full max-w-[560px]">
            <img
              src="/jordievoorje-logo.png"
              alt="JorDieVoorJe"
              className="w-full h-auto select-none block"
              draggable={false}
              data-testid="hero-logo"
            />
            <HeroSocials />
          </div>

          <p className="mt-10 text-lg text-zinc-300 max-w-xl">
            {bio.tagline}{' '}
            <span className="text-zinc-400">{bio.description}</span>
          </p>

          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="relative inline-block">
              <HeroSpeechBubble
                className="hidden sm:block animate-[float_4s_ease-in-out_infinite]"
                style={{
                  position: 'absolute',
                  right: 'calc(100% + 10px)',
                  top: '18%',
                  zIndex: 4,
                  width: 'max-content',
                  maxWidth: 'min(220px, 38vw)',
                  transformOrigin: 'center',
                }}
              />
              <img
                src={mascotUrl}
                alt="JorDievoorje mascot"
                data-testid="hero-logo"
                className="w-32 sm:w-40 md:w-44 lg:w-48 h-auto drop-shadow-[0_10px_30px_rgba(168,85,247,0.55)] animate-[float_4s_ease-in-out_infinite] block"
              />
              <HeartRateBubble
                size={72}
                className="animate-[float_4s_ease-in-out_infinite]"
                style={{
                  position: 'absolute',
                  top: '14%',
                  left: 'calc(100% + 30px)',
                  zIndex: 5,
                  transformOrigin: 'center',
                }}
              />
            </div>
            <LiveStatusBadge
              isLive={live.isLive}
              twitchUrl={streamer.socials.twitch}
            />
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={streamer.socials.twitch}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-colors"
            >
              <Play className="w-4 h-4 fill-current" /> Kijk op Twitch
            </a>
            <a
              href={streamer.socials.discord}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Join Discord
            </a>
            <a
              href={streamer.socials.youtube}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-colors"
            >
              <Youtube className="w-4 h-4" /> YouTube
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl" data-testid="hero-stats-grid">
            {loading
              ? [0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="px-4 py-3 rounded-lg bg-white/[0.03] border border-white/10 h-[76px] flex flex-col justify-between"
                    data-testid={`hero-stat-skeleton-${i}`}
                  >
                    <div className="h-7 w-16 rounded bg-white/5 animate-pulse" />
                    <div className="h-3 w-12 rounded bg-white/5 animate-pulse mt-1" />
                  </div>
                ))
              : stats.map((s) => (
                  <div
                    key={s.label}
                    className="px-4 py-3 rounded-lg bg-white/[0.03] border border-white/10"
                  >
                    {s.image ? (
                      <img
                        src={s.image}
                        alt={s.value}
                        title={s.value}
                        className="w-10 h-14 object-cover rounded-sm border border-white/10 mb-1"
                      />
                    ) : (
                      <div className="text-2xl font-bold text-white truncate">
                        {s.value}
                      </div>
                    )}
                    <div className="text-xs uppercase tracking-wider text-zinc-400 mt-0.5 truncate">
                      {s.image ? s.value : s.label}
                    </div>
                  </div>
                ))}
          </div>

          <LatestStatsGrid />

          <div className="mt-auto">
            <Subgoal />
          </div>
        </div>

        <div className="relative flex flex-col">
          {!user && (
            <button
              type="button"
              onClick={login}
              data-testid="hero-login-btn"
              className="w-full mb-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold border border-violet-400/40 shadow-lg shadow-violet-900/40 transition-colors"
            >
              Inloggen om te stemmen &amp; suggesties te doen
            </button>
          )}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-900/60 shadow-2xl shadow-violet-900/30">
            <div className="relative aspect-video bg-zinc-900">
              {playerOn && live.isLive ? (
                <>
                  <iframe
                    key={broadcasterLogin}
                    src={twitchEmbedSrc}
                    title={`${broadcasterName} live`}
                    frameBorder="0"
                    allowFullScreen
                    scrolling="no"
                    allow="autoplay; fullscreen"
                    className="absolute inset-0 w-full h-full"
                    data-testid="hero-twitch-player"
                  />
                  <button
                    type="button"
                    onClick={() => setPlayerOn(false)}
                    className="absolute bottom-3 left-3 px-3 py-1.5 rounded-lg text-xs font-semibold bg-black/70 backdrop-blur border border-white/10 text-zinc-100 hover:bg-black/90 z-10"
                    data-testid="hero-close-player"
                  >
                    Sluit speler
                  </button>
                </>
              ) : (
                <>
                  {loading ? (
                    <div className="w-full h-full animate-pulse bg-gradient-to-br from-zinc-800/60 to-zinc-900" />
                  ) : live.thumb ? (
                    <img
                      src={live.thumb}
                      alt="latest stream"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-violet-900/30 to-zinc-900" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
                  {live.isLive ? (
                    <button
                      type="button"
                      onClick={() => setPlayerOn(true)}
                      className="absolute inset-0 flex items-center justify-center group"
                      data-testid="hero-play-live"
                      aria-label="Bekijk live stream"
                    >
                      <span className="w-16 h-16 rounded-full bg-red-500/90 backdrop-blur-md border border-white/40 flex items-center justify-center group-hover:scale-110 group-hover:bg-red-500 transition-all shadow-lg shadow-red-900/40">
                        <Play className="w-7 h-7 text-white fill-current ml-1" />
                      </span>
                      <span className="absolute bottom-6 px-3 py-1 rounded-full text-xs font-semibold bg-black/60 backdrop-blur text-white">
                        Bekijk live in de speler
                      </span>
                    </button>
                  ) : (
                    <a
                      href={streamer.socials.twitch}
                      target="_blank"
                      rel="noreferrer"
                      className="absolute inset-0 flex items-center justify-center group"
                    >
                      <span className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:scale-110 group-hover:bg-violet-600 transition-all">
                        <Play className="w-7 h-7 text-white fill-current ml-1" />
                      </span>
                    </a>
                  )}
                </>
              )}
              {!loading && (
                <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                  {live.isLive ? (
                    <span className="px-2 py-1 rounded text-xs font-bold bg-red-500 text-white flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      LIVE
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded text-xs font-bold bg-zinc-800/80 backdrop-blur text-zinc-200">
                      OFFLINE
                    </span>
                  )}
                  {live.category && (
                    <span className="px-2 py-1 rounded text-xs bg-black/50 backdrop-blur text-white">
                      {live.category}
                    </span>
                  )}
                </div>
              )}
              {live.isLive && (
                <div className="absolute top-3 right-3 px-2 py-1 rounded text-xs bg-black/60 backdrop-blur text-white flex items-center gap-1 z-10">
                  <Users className="w-3 h-3" /> {live.viewers}
                </div>
              )}
            </div>
            <div className="p-5">
              {loading ? (
                <>
                  <div className="h-5 w-3/4 rounded bg-zinc-800/80 animate-pulse" />
                  <div className="h-3 w-24 rounded bg-zinc-800/80 animate-pulse mt-2" />
                </>
              ) : (
                <>
                  <h3 className="text-white font-semibold text-lg leading-snug">
                    {live.title || '\u2014'}
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    {live.startedAt}
                  </p>
                </>
              )}
            </div>
          </div>

          <HomeMultiChat broadcasterLogin={broadcasterLogin} />

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/doneren"
              data-testid="hero-donate-btn"
              className="group relative flex w-full overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-fuchsia-500/15 via-violet-500/[0.08] to-orange-500/15 px-5 py-5 shadow-2xl shadow-violet-900/20 hover:border-fuchsia-400/40 hover:shadow-fuchsia-500/20 transition-all"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-fuchsia-500/30 blur-3xl pointer-events-none group-hover:bg-fuchsia-500/50 transition-colors" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-violet-600/30 blur-3xl pointer-events-none" />

              <div className="relative w-full flex items-center gap-4">
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-orange-400 flex items-center justify-center shadow-lg shadow-fuchsia-900/40 group-hover:scale-110 transition-transform">
                  <Euro className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-fuchsia-300 mb-0.5">
                    Steun de stream
                  </div>
                  <div className="text-lg font-extrabold text-white leading-tight">
                    Doneer{' '}
                    <span className="bg-gradient-to-r from-fuchsia-300 via-violet-200 to-orange-200 bg-clip-text text-transparent">
                      centjes
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1 line-clamp-1">
                    Elke dono helpt de stream
                  </p>
                </div>
              </div>
            </Link>

            <Link
              to="/bits"
              data-testid="hero-bits-btn"
              className="group relative flex w-full overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-cyan-500/15 via-purple-500/[0.08] to-violet-500/15 px-5 py-5 shadow-2xl shadow-purple-900/20 hover:border-cyan-400/40 hover:shadow-cyan-500/20 transition-all"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-500/30 blur-3xl pointer-events-none group-hover:bg-cyan-500/50 transition-colors" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-violet-600/30 blur-3xl pointer-events-none" />

              <div className="relative w-full flex items-center gap-4">
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400 via-purple-500 to-violet-500 flex items-center justify-center shadow-lg shadow-purple-900/40 group-hover:scale-110 transition-transform">
                  <Gem className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-300 mb-0.5">
                    Cheer in chat
                  </div>
                  <div className="text-lg font-extrabold text-white leading-tight">
                    Smijt{' '}
                    <span className="bg-gradient-to-r from-cyan-300 via-purple-300 to-violet-300 bg-clip-text text-transparent">
                      bits
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1 line-clamp-1">
                    Bekijk leaderboard &amp; info
                  </p>
                </div>
              </div>
            </Link>
          </div>

          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-violet-600 -z-10 blur-2xl" />
        </div>
      </div>
    </section>
  );
}
